import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { ThemeProvider } from 'styled-components';
import { autorun } from 'mobx';
import { Provider, observer } from 'mobx-react';
import { Transition, TransitionGroup } from 'react-transition-group';
import { withPrefix } from 'gatsby-link';
import { I18nextProvider } from 'react-i18next';
import i18n from '../i18n';

// fetch polyfill
import 'whatwg-fetch';

//global css injections
import './reset.css';
import './global.css';
import '../components/global/typography/font-defs';
import '../components/global/global-settings/scroll-styles';

//css
import breakpoints from '../vars/breakpoints';
import GridDemo from '../components/global/grid/grid-demo';
import { BODY } from '../components/global/typography/typography';

//data
import allData from '../data/data.json';

//components
import Header from '../components/header/header';
import Footer from '../components/footer/Footer';
import CSOverlay from '../components/modules/CSOverlay/CSOverlay';
import TransitionHandler from '../components/TransitionHandler/TransitionHandler';

//mobx
import store from '../store/store';

// favicon
import faviconIco from '../static/images/favicon.ico';
import faviconPng from '../static/images/favicon.png';
import favicon72 from '../static/images/favicon72.png';
import favicon114 from '../static/images/favicon114.png';
import favicon144 from '../static/images/favicon144.png';

let location = null;

// page transitions
const defaultStyle = {
  transition: `opacity 500ms cubic-bezier(.215,.61,.355,1)`,
  opacity: 1,
  position: 'relative',
  zIndex: 2,
}

// autorun nom modal scroll points
autorun(() => {
  if(store.nominationModal === true) {
    if (typeof window !== `undefined`) {
      const bodyScrollDistance = window.pageYOffset;
      store.setBodyScrollDistance(bodyScrollDistance);
      if(window.dataLayer) {
        window.dataLayer.push({
          'event': 'nominate-modal:view',
          'modalView': 'Enter Zip Code'
        });
      }
    }
  } else {
    if (typeof window !== `undefined`) {
      const modalScrollDistance = window.pageYOffset;
      store.setModalScrollDistance(modalScrollDistance);
      if(window.dataLayer) {
        window.dataLayer.push({
          'event': 'nominate-modal:click',
          'modalCTA': 'X'
        });
      }
    }
  }
});

@observer class Layout extends Component {
  constructor(props) {
    super(props);
    // turn array to dictionary, much easier to work with (graphql has no ez way to return this)
    this.imageDictionary = props.data.images.edges.reduce( (map, obj) => {
      map[obj.node.name] = obj.node.childImageSharp;
      return map;
    }, {} );
    this.videoDictionary = props.data.videos.edges.reduce( (map, obj) => {
      map[obj.node.name] = obj.node.publicURL;
      return map;
    }, {} );
    this.checkMobile = this.checkMobile.bind(this);

    this.topForPageTrans = 0;

    this.firstLoad = true;
  }
  componentDidMount() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    if (typeof window !== `undefined`) {
      this.googleTagManager(window,document,'script','dataLayer','GTM-NGDXSG2');
    }
  }
  googleTagManager(w,d,s,l,i) {
    w[l] = w[l] || [];
    w[l].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });
    w[l].push({
      'event': 'language-preference:load',
      'language': navigator.language
    });
    var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : '';
        j.async = true;
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
        f.parentNode.insertBefore(j, f);

  }
  checkMobile() {
    const browserWidth = window.innerWidth;
    store.checkMobile(browserWidth);
  }
  componentDidUpdate() {
    if(store.modalTransitionState === 'exiting') {
      window.scrollTo(0, store.bodyScrollDistance);
    }
    if(store.modalTransitionState === 'entered') {
      window.scrollTo(0, 0);
  }
  }
  transitionStyles(state) {
    if(state === 'exited' || state === 'entered') {
      if (typeof window !== `undefined`) {
        this.topForPageTrans = window.pageYOffset;
      }
    }
    if(!this.firstLoad) {
      switch(state) {
        case 'entering':
          return { opacity: 1, zIndex: 1 };
          break;
        case 'entered':
          return { opacity: 1 };
          break;
        case 'exiting':
          return { opacity: 0, position:'fixed', top:`-${this.topForPageTrans}px`, left: 0, width: '100%', zIndex: 3};
          break;
        case 'exited':
          return { opacity: 0, zIndex: 1};
          break;
      }
    } else {
      this.firstLoad = false;
      return { opacity: 1, zIndex: 1 };
    }
  }
  render() {
    const { data, children, location } = this.props;
    return (
      <I18nextProvider i18n={i18n}>
        <Provider store={store}>
          <div>
            <Helmet
              title={allData.siteTitle}
              meta={[
                { property: 'og:title', content: `${allData.siteTitle}`},
                { property: 'og:description', content: `${allData.siteDescription}` },
                { property: 'og:image', content: `https://pavingforpizza.com${withPrefix('/DOM_P4P_Facebook_Social.jpg')}` },
                { property: 'og:url', content: `https://pavingforpizza.com${location.pathname}`},
                { name: 'twitter:card', content: 'summary_large_image' },
              ]}
            >
              <noscript>{`<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-NGDXSG2" height="0" width="0" style="display:none;visibility:hidden;"></iframe>`}</noscript>
              <link rel="shortcut icon" href={faviconIco} />
              <link rel="apple-touch-icon" href={faviconPng} />
              <link rel="apple-touch-icon" sizes="72x72" href={favicon72} />
              <link rel="apple-touch-icon" sizes="114x114" href={favicon114} />
              <link rel="apple-touch-icon" sizes="144x144" href={favicon144} />
            </Helmet>
            <ThemeProvider
              theme={{
                breakpoints: [
                  breakpoints['bp-mobile'],
                  breakpoints['bp-tablet'],
                  breakpoints['bp-desktop'],
                  breakpoints['bp-desktop-l']
                ],
              }}
            >
              <div>
                { location.search.includes('grid=true') &&
                  <GridDemo />
                }
                <Header siteTitle={allData.siteTitle} data={allData} />
                <Transition
                  in={store.csMenu}
                  timeout={300}
                  onEnter={node => node.offsetHeight}
                  mountOnEnter
                  unmountOnExit
                >
                  {(state) => (
                    <CSOverlay transitionState={state} data={allData.pages} location={location} />
                  )}
                </Transition>
                <BODY style={
                  store.modalTransitionState === 'entered' ? {overflowX: 'hidden', overflowY: 'hidden', height: '0px'} : {overflowY: 'visible', height: '100vh'}
                }>
                  <TransitionGroup key="transition-group">
                    <Transition
                      key={location.pathname}
                      mountOnEnter
                      appear
                      unmountOnExit
                      timeout={500}
                    >
                    {(state) => {
                      return  (
                          <div style={{
                            ...defaultStyle,
                            ...this.transitionStyles(state)
                          }}>
                            <TransitionHandler location={location}>
                              <div>
                                {
                                children( {
                                  ...this.props,
                                  imageDictionary: this.imageDictionary,
                                  videoDictionary: this.videoDictionary
                                  } )
                                }
                              </div>
                            </TransitionHandler>
                          </div>
                        )
                      }
                    }
                    </Transition>
                  </TransitionGroup>
                  <Footer data={allData.footer} />
                </BODY>
              </div>
            </ThemeProvider>
          </div>
        </Provider>
      </I18nextProvider>
    )
  }
}

Layout.propTypes = {
  children: PropTypes.func,
}

export default Layout;

export const query = graphql`
  query AllMedia {
    images: allFile(filter: { absolutePath: {regex : "\/images/"} }) {
      edges {
        node {
          name,
          childImageSharp {
            sizes(maxWidth: 1240 ) {
              ...GatsbyImageSharpSizes_withWebp
            }
          }
        }
      }
    }
    videos: allFile(filter: { absolutePath: {regex : "\/videos/"} }) {
      edges {
        node {
          name,
          publicURL
        }
      }
    }
  }
`;


// WEBPACK FOOTER //
// ./src/layouts/index.js